/**
 * @biomax/grounding/claim-check — the ONE canonical citation claim-check.
 *
 * This is the single source of truth for "does a citation note's CLAIM
 * (the author / year named in the prose) match the paper the PMID/DOI
 * actually resolved to?" — the anti-hallucination check that caught the
 * marburg→homeopathy, ebola→myeloproliferative, strep→COPD and
 * abiraterone Guillemette→Benoist wrong-PMID corruptions.
 *
 * BACKGROUND — why this is parameterized rather than one fixed function.
 * The check was independently grown in THREE places during parallel
 * builds (the pathogen catalog verifier, the drug catalog verifier, and
 * the grounding spine's `claimMatch`). Each tuned its own surname-parse
 * forms, noise vocabulary and author-match mode to its own corpus, and
 * each is pinned by regression fixtures. Collapsing them to a single
 * un-parameterized function would silently change behaviour (e.g. the
 * drug parser reads "Holford NHG 1986" as both `holford` AND `nhg`; the
 * pathogen parser reads only `nhg`; grounding parses every capitalised
 * word in original case). So the canonical engine below is ONE algorithm
 * whose surface-tuning is supplied by a `ClaimCheckPolicy`. Each consumer
 * binds its own policy and gets byte-identical behaviour to its pre-dedup
 * implementation — a genuine union of strengths, with NO behaviour change
 * and NO weakening of any gate.
 *
 * Pure + browser-safe: NO node:* imports. The network round-trip that
 * resolves a PMID lives in the catalog verifiers / @biomax/grounded-tools;
 * this module takes already-resolved metadata and decides the verdict.
 */

// ---------------------------------------------------------------------------
// Result types (the rich shape the catalog verifiers consume)
// ---------------------------------------------------------------------------

/**
 * Outcome of cross-checking a citation note's claimed author/year against
 * the resolved paper's metadata.
 *   - 'match'        — a claimed surname and/or year is consistent with the
 *                      resolved paper (author is the authoritative signal).
 *   - 'mismatch'     — a parseable claimed author surname is present but is
 *                      in NEITHER the resolved author list NOR the title (the
 *                      integrity failure callers hard-fail on); a coincidental
 *                      year does NOT rescue a wrong paper.
 *   - 'uncheckable'  — the note carries no parseable author surname to check
 *                      (a DERIVED/computed note, or only a bare year) — soft.
 *   - 'not-resolved' — the PMID/DOI itself did not resolve (no metadata).
 */
export type ClaimCheckVerdict = 'match' | 'mismatch' | 'uncheckable' | 'not-resolved';

export interface ClaimCheckResult {
  verdict: ClaimCheckVerdict;
  /** Claimed author surname(s) parsed from the note. */
  claimedSurnames: string[];
  /** Claimed year(s) parsed from the note. */
  claimedYears: number[];
  /** Human-readable explanation (esp. for mismatch). */
  detail: string;
}

/** Resolved-paper metadata the check runs against. */
export interface ResolvedClaimMeta {
  authors?: string[];
  year?: number;
  title?: string;
}

// ---------------------------------------------------------------------------
// Policy — the per-consumer surface tuning (the only thing that varies)
// ---------------------------------------------------------------------------

/**
 * How the note's claimed surnames are extracted.
 *   - 'anchored' — surnames are recognised only in specific attribution
 *     FORMS ("Surname et al", "Surname YYYY", "Surname & Surname", and —
 *     when `drugForms` is set — "Surname IJ" initials-follow and
 *     "Surname, Surname"). Output normalised lowercase. This is the
 *     catalog approach: it ignores ordinary capitalised prose, so a note
 *     describing a parameter does not produce spurious surnames.
 *   - 'greedy' — every capitalised word (>=3 letters, not all-caps, not a
 *     STOP word) is a candidate, in ORIGINAL case. This is the grounding
 *     spine approach: simpler, used where notes are short attributions.
 */
export type SurnameParseStrategy = 'anchored' | 'greedy';

/** How a claimed surname is compared to a resolved-author family name. */
export type AuthorMatchMode =
  /** Substring either way (rf includes cs OR cs includes rf) — catalog. */
  | 'substring'
  /** Whole-token equality only — grounding ("cohen" != "cohenstein"). */
  | 'whole-token';

export interface ClaimCheckPolicy {
  /** Surname extraction strategy. */
  parseStrategy: SurnameParseStrategy;
  /** Lowercase noise words rejected as surnames (journals / orgs / prose). */
  noise: ReadonlySet<string>;
  /** ('anchored' only) also recognise the drug-note "Surname IJ" + "Surname, Surname" forms. */
  drugForms?: boolean;
  /** ('anchored' only) reject hyphenated tokens whose leading segment is noise (FDA-label). */
  rejectHyphenNoiseHead?: boolean;
  /** Author-vs-claim comparison mode. */
  authorMatchMode: AuthorMatchMode;
  /** Whether a claimed surname appearing in the resolved TITLE counts as a match. */
  titleMatch: boolean;
  /**
   * ('substring' mode) also `match` when the note text literally contains a
   * resolved first-author family name as a word — catches short (<=2 char)
   * family names the surname parser drops (drug-db verbatim-short-author check).
   */
  verbatimResolvedAuthor?: boolean;
  /**
   * When the claimed author is not matched AND the resolved record exposes NO
   * author list (book-chapter/dataset DOI), return 'uncheckable' instead of
   * 'mismatch' — absence of metadata is not evidence of a wrong paper.
   */
  noAuthorGuard?: boolean;
  /** Dedup parsed years (catalog) vs keep order+duplicates (grounding). */
  dedupYears: boolean;
  /**
   * Verdict when ONLY a year is parseable (no surname) and metadata has a year:
   *   - 'uncheckable' — a year alone is too weak to confirm/refute (catalog +
   *     grounding's documented stance via the surname path).
   *   - 'compare' — match iff the years agree (grounding's bare-year branch).
   */
  yearOnlyVerdict: 'uncheckable' | 'compare';
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

/** Strip diacritics + lowercase (é→e, ñ→n, Görg→gorg). Preserves hyphens /
 *  apostrophes (so "Henao-Restrepo" → "henao-restrepo"). */
export function normName(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Like {@link normName} but ALSO strips every non-letter (hyphens, apostrophes,
 *  digits) so "Henao-Restrepo" / "O'Driscoll" collapse to bare letter tokens.
 *  Used by the whole-token author match so a claimed hyphenated surname matches
 *  the same author whether or not the metadata keeps the hyphen. */
export function normToken(s: string): string {
  return normName(s).replace(/[^a-z]/g, '');
}

/**
 * Candidate lowercase family-name token(s) from a resolved-author string.
 * PubMed/Crossref return authors as "Family Initials" ("Holford NHG") OR
 * "Given M Family" ("Karen Kotloff"); we cannot always tell which, so we
 * return BOTH the first and the last token (minus a trailing initials block).
 * Returning extra candidates can only RELAX the check (avoid false-positive
 * mismatches), never tighten it.
 */
export function authorFamilyCandidates(a: string): string[] {
  const cleaned = a.replace(/\.$/, '').trim();
  const toks = cleaned.split(/\s+/).filter(Boolean);
  if (toks.length === 0) return [];
  const out = new Set<string>();
  const last = toks[toks.length - 1]!;
  const isInitials = (t: string) => /^[A-Z][A-Za-z]{0,3}$/.test(t) && t.length <= 3;
  if (toks.length >= 2 && isInitials(last)) {
    out.add(normName(toks.slice(0, -1).join(' ')));
    out.add(normName(toks[0]!));
  } else {
    out.add(normName(last));
    out.add(normName(toks[0]!));
  }
  return Array.from(out).filter((s) => s.length > 1);
}

/**
 * Whole, significant (>=3-letter) name tokens across a set of author strings,
 * normalised. Order-independent ("Daniel G Bausch" and "Bausch DG" both yield
 * `bausch`). Used by the 'whole-token' author-match mode (grounding).
 */
export function authorTokens(authors: readonly string[]): Set<string> {
  const out = new Set<string>();
  for (const a of authors) {
    for (const tok of a.split(/[\s,]+/)) {
      const n = normToken(tok);
      if (n.length >= 3) out.add(n);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Surname / year parsing (policy-driven)
// ---------------------------------------------------------------------------

const GREEDY_RE = /\b([A-Z][a-zA-Z'’-]{2,})\b/g;

/**
 * Parse claimed author surname(s) from a citation note, per the policy's
 * parse strategy + noise set. See `SurnameParseStrategy`.
 */
export function parseClaimedSurnamesWith(note: string, policy: ClaimCheckPolicy): string[] {
  if (policy.parseStrategy === 'greedy') {
    // Every capitalised word (>=3 letters), ORIGINAL case, not all-caps, not noise.
    const out: string[] = [];
    let m: RegExpExecArray | null;
    GREEDY_RE.lastIndex = 0;
    while ((m = GREEDY_RE.exec(note)) !== null) {
      const w = m[1]!;
      if (!/^[A-Z]+$/.test(w) && !policy.noise.has(normName(w))) out.push(w);
    }
    return out;
  }
  // anchored — normalised lowercase output.
  const out = new Set<string>();
  const add = (raw: string) => {
    const n = normName(raw);
    if (n.length <= 2 || policy.noise.has(n)) return;
    if (policy.rejectHyphenNoiseHead) {
      const head = n.split(/[-'’]/)[0]!;
      if (head !== n && policy.noise.has(head)) return;
    }
    out.add(n);
  };
  // "Surname et al"
  for (const m of note.matchAll(/\b([A-Z][a-zA-ZÀ-ſ'’-]{2,})\s+et\s+al\.?/g)) add(m[1]!);
  // "Surname & Surname" / "Surname and Surname"
  for (const m of note.matchAll(/\b([A-Z][a-zA-ZÀ-ſ'’-]{2,})\s*(?:&|and)\s*([A-Z][a-zA-ZÀ-ſ'’-]{2,})/g)) {
    add(m[1]!); add(m[2]!);
  }
  // "Surname YYYY"
  for (const m of note.matchAll(/\b([A-Z][a-zA-ZÀ-ſ'’-]{2,})\s+(?:1[89]\d\d|20[0-4]\d)\b/g)) add(m[1]!);
  if (policy.drugForms) {
    // "Surname I" / "Surname IJ" / "Surname IJK" — initials-follow attribution.
    for (const m of note.matchAll(/\b([A-Z][a-zA-ZÀ-ſ'’-]{2,})\s+([A-Z]{1,3})\b/g)) add(m[1]!);
    // "Surname, Surname" — comma-separated author-list form.
    for (const m of note.matchAll(/\b([A-Z][a-zA-ZÀ-ſ'’-]{2,}),\s*([A-Z][a-zA-ZÀ-ſ'’-]{2,})/g)) {
      add(m[1]!); add(m[2]!);
    }
  }
  return Array.from(out);
}

const YEAR_RE_CATALOG = /\b(1[89]\d\d|20[0-4]\d)\b/g;
const YEAR_RE_BROAD = /\b(19\d{2}|20\d{2})\b/g;

/** Parse claimed publication year(s) from a citation note, per the policy. */
export function parseClaimedYearsWith(note: string, policy: ClaimCheckPolicy): number[] {
  const re = policy.dedupYears ? YEAR_RE_CATALOG : YEAR_RE_BROAD;
  const nums = Array.from(note.matchAll(re)).map((m) => Number(m[1]));
  return policy.dedupYears ? Array.from(new Set(nums)) : nums;
}

// ---------------------------------------------------------------------------
// The canonical cross-check
// ---------------------------------------------------------------------------

/**
 * Decide whether a citation note's CLAIM matches the resolved paper.
 *
 * AUTHOR is the authoritative signal: the resolved paper's author list is
 * knowable, so a claimed surname that appears in NEITHER the author list NOR
 * (when `policy.titleMatch`) the title means the wrong paper — even when a
 * year happens to coincide. A coincidental year must NOT rescue a wrong paper.
 * The match is lenient (ANY claimed surname matching ANY resolved author
 * counts) so a multi-cite note whose primary citation IS this PMID is not a
 * false mismatch.
 */
export function crossCheckClaimWith(
  note: string | undefined,
  resolved: ResolvedClaimMeta | null,
  policy: ClaimCheckPolicy,
): ClaimCheckResult {
  const surnames = note ? parseClaimedSurnamesWith(note, policy) : [];
  const years = note ? parseClaimedYearsWith(note, policy) : [];

  if (!resolved) {
    return { verdict: 'not-resolved', claimedSurnames: surnames, claimedYears: years, detail: 'PMID did not resolve' };
  }

  const resolvedFamilies = (resolved.authors ?? []).flatMap(authorFamilyCandidates);
  const resolvedTokens = policy.authorMatchMode === 'whole-token'
    ? authorTokens(resolved.authors ?? [])
    : null;
  const titleLc = normName(resolved.title ?? '');
  const resAttr = `${(resolved.authors ?? [])[0] ?? '?'} ${resolved.year ?? '?'}`;
  const resTitle = (resolved.title ?? '').slice(0, 90);

  // Compare ONE claimed surname (already in the parser's output form) to the
  // resolved author set per the policy's match mode.
  const matchesAuthor = (cs: string): boolean => {
    if (policy.authorMatchMode === 'whole-token') {
      // Whole-token (grounding): strip non-letters BOTH sides so a hyphenated
      // claimed surname ("Henao-Restrepo") matches the same author token.
      const ct = normToken(cs);
      return ct.length >= 3 && resolvedTokens!.has(ct);
    }
    const csn = normName(cs); // tolerate original-case (greedy) + lowercase (anchored)
    if (csn.length < 1) return false;
    return resolvedFamilies.some((rf) => rf === csn || rf.includes(csn) || csn.includes(rf));
  };
  const matchesTitle = (cs: string): boolean => policy.titleMatch && titleLc.includes(normName(cs));

  // Verbatim short-author corroboration (drug-db): does the note literally
  // name a resolved first-author family as a word? Catches <=2-char families
  // ("Fu G", "Wu B") the surname parser drops. Runs BEFORE the no-surname
  // early-out, and strengthens the gate generally.
  if (policy.verbatimResolvedAuthor) {
    const noteLc = normName(note ?? '');
    const noteNamesResolvedAuthor = resolvedFamilies.some((rf) =>
      rf.length >= 2 && new RegExp(`\\b${rf.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(noteLc),
    );
    if (noteNamesResolvedAuthor) {
      return { verdict: 'match', claimedSurnames: surnames, claimedYears: years,
        detail: `note names the resolved author verbatim (consistent with ${(resolved.authors ?? [])[0] ?? '?'} ${resolved.year ?? '?'})` };
    }
  }

  if (surnames.length === 0) {
    if (policy.yearOnlyVerdict === 'compare' && years.length > 0 && resolved.year != null) {
      const ok = years.includes(resolved.year);
      return { verdict: ok ? 'match' : 'mismatch', claimedSurnames: surnames, claimedYears: years,
        detail: ok ? `claimed year [${years.join('/')}] matches ${resolved.year}` : `claimed year [${years.join('/')}] != resolved ${resolved.year}` };
    }
    return {
      verdict: 'uncheckable', claimedSurnames: surnames, claimedYears: years,
      detail: years.length > 0
        ? `note has a year [${years.join('/')}] but no parseable author surname (consortium/DERIVED) — year alone is too weak to cross-check`
        : 'note has no parseable author surname to cross-check (DERIVED/computed or value-only note)',
    };
  }

  const titleHit = surnames.some(matchesTitle);
  const authorMatch = titleHit || surnames.some(matchesAuthor);

  // No-author guard (drug-db): book-chapter/dataset DOIs resolve with an empty
  // author list — cannot mechanically cross-check, so 'uncheckable' (soft), not
  // a false mismatch. A title hit above still upgrades to 'match'.
  if (policy.noAuthorGuard && !authorMatch && resolvedFamilies.length === 0
      && (policy.authorMatchMode !== 'whole-token' || resolvedTokens!.size === 0)) {
    return { verdict: 'uncheckable', claimedSurnames: surnames, claimedYears: years,
      detail: `resolved record exposes no author list (book-chapter/dataset DOI?) and the claimed author [${surnames.join('/')}] is not in the title "${(resolved.title ?? '').slice(0, 70)}" — cannot mechanically cross-check (soft)` };
  }

  const yearMatch = years.length > 0 && resolved.year != null
    && years.some((y) => Math.abs(y - resolved.year!) <= 1);

  if (authorMatch) {
    return {
      verdict: 'match', claimedSurnames: surnames, claimedYears: years,
      detail: `claim [${surnames.join('/')}${years.length ? ' ' + years.join('/') : ''}] consistent with ${resAttr}${years.length && !yearMatch ? ' (author matches; claimed year differs — likely e-pub vs print or multi-cite note)' : ''}`,
    };
  }
  return {
    verdict: 'mismatch', claimedSurnames: surnames, claimedYears: years,
    detail: `claimed author [${surnames.join('/')}] is in NEITHER the author list NOR the title of resolved paper: "${resTitle}" (${resAttr})${yearMatch ? ' — note the year coincides, but a coincidental year does not make it the claimed paper' : ''}`,
  };
}
